import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Layout from '../../../components/feature/Layout';
import { useAuth } from '../../../hooks/useAuth';
import { createUsageRecipe } from '../../../repositories/recipes';
import { fetchMaterials } from '../../../repositories/materials';
import { fetchSites } from '../../../repositories/sites';
import type { Material } from '../../../types/materials';
import type { Site } from '../../../types/sites';

export default function NewRecipePage() {
  const navigate = useNavigate();
  const { user, loading: authLoading } = useAuth();

  // フォームデータ
  const [title, setTitle] = useState('');
  const [summary, setSummary] = useState('');
  const [subject, setSubject] = useState('');
  const [gradeMin, setGradeMin] = useState<number | null>(null);
  const [gradeMax, setGradeMax] = useState<number | null>(null);
  const [scene, setScene] = useState('');
  const [groupType, setGroupType] = useState('');
  const [timeMinutes, setTimeMinutes] = useState<number | null>(null);
  const [steps, setSteps] = useState('');
  const [tips, setTips] = useState('');
  const [cautions, setCautions] = useState('');
  const [role, setRole] = useState('');
  const [authorName, setAuthorName] = useState('');
  const [materialId, setMaterialId] = useState<string | null>(null);
  const [siteId, setSiteId] = useState<string | null>(null);

  // 選択肢データ
  const [materials, setMaterials] = useState<Material[]>([]);
  const [sites, setSites] = useState<Site[]>([]);

  // UI状態
  const [submitting, setSubmitting] = useState(false);
  const [errors, setErrors] = useState<string[]>([]);

  // ログインガード
  useEffect(() => {
    if (!authLoading && !user) {
      navigate('/login?redirect=/recipes/new');
    }
  }, [authLoading, user, navigate]);

  // 単元ガイド・教材サイトの取得
  useEffect(() => {
    const loadOptions = async () => {
      try {
        const [materialsData, sitesData] = await Promise.all([
          fetchMaterials(),
          fetchSites(),
        ]);
        setMaterials(materialsData);
        setSites(sitesData);
      } catch (error) {
        console.error('選択肢の取得に失敗しました:', error);
      }
    };

    loadOptions();
  }, []);

  // バリデーション
  const validate = (): boolean => {
    const newErrors: string[] = [];

    if (!title.trim()) {
      newErrors.push('タイトルは必須です');
    }

    if (!steps.trim()) {
      newErrors.push('手順は必須です');
    }

    if (gradeMin !== null && gradeMax !== null && gradeMin > gradeMax) {
      newErrors.push('学年の範囲が正しくありません（最小値が最大値より大きくなっています）');
    }

    if (!scene) {
      newErrors.push('シーンは必須です');
    }

    setErrors(newErrors);
    return newErrors.length === 0;
  };

  // 送信処理
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validate()) {
      window.scrollTo({ top: 0, behavior: 'smooth' });
      return;
    }

    try {
      setSubmitting(true);
      setErrors([]);

      await createUsageRecipe({
        title: title.trim(),
        summary: summary.trim() || undefined,
        subject: subject || undefined,
        grade_min: gradeMin,
        grade_max: gradeMax,
        scene: scene || undefined,
        group_type: groupType || undefined,
        time_minutes: timeMinutes,
        steps: steps.trim(),
        tips: tips.trim() || undefined,
        cautions: cautions.trim() || undefined,
        role: role || undefined,
        author_name: authorName.trim() || undefined,
        material_id: materialId || undefined,
        site_id: siteId || undefined,
      });

      // 成功時は自分の投稿レシピページへ遷移
      navigate('/account/my-recipes');
    } catch (error) {
      console.error('レシピの投稿に失敗しました:', error);
      setErrors(['レシピの投稿に失敗しました。時間をおいてもう一度お試しください。']);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } finally {
      setSubmitting(false);
    }
  };

  // 認証情報取得中
  if (authLoading) {
    return (
      <Layout>
        <div className="min-h-screen bg-gray-50 flex items-center justify-center">
          <div className="text-center">
            <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-teal-600 mb-4"></div>
            <p className="text-gray-600">読み込み中...</p>
          </div>
        </div>
      </Layout>
    );
  }

  // 未ログイン（リダイレクト前）
  if (!user) {
    return null;
  }

  return (
    <Layout>
      <div className="min-h-screen bg-gray-50 py-12">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* ヘッダー */}
          <div className="mb-8">
            <h1 className="text-3xl font-bold text-gray-900 mb-2">
              活用レシピを投稿する
            </h1>
            <p className="text-gray-600">
              教材や教材サイトの具体的な活用方法を共有しましょう。投稿後、運営チームが内容を確認してから公開されます。
            </p>
          </div>

          {/* エラーメッセージ */}
          {errors.length > 0 && (
            <div className="mb-6 p-4 rounded-lg bg-red-50 border border-red-200">
              <h3 className="text-sm font-medium text-red-800 mb-2">
                以下の項目を確認してください：
              </h3>
              <ul className="list-disc list-inside text-sm text-red-700 space-y-1">
                {errors.map((error, index) => (
                  <li key={index}>{error}</li>
                ))}
              </ul>
            </div>
          )}

          {/* フォーム */}
          <form onSubmit={handleSubmit} className="bg-white rounded-lg shadow-sm p-6 space-y-6">
            {/* タイトル */}
            <div>
              <label htmlFor="title" className="block text-sm font-medium text-gray-700 mb-1">
                タイトル <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                id="title"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder="例：算数ゲームで楽しく九九を覚える"
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500"
                maxLength={100}
              />
              <p className="mt-1 text-xs text-gray-500">
                {title.length}/100文字
              </p>
            </div>

            {/* 概要 */}
            <div>
              <label htmlFor="summary" className="block text-sm font-medium text-gray-700 mb-1">
                概要（任意）
              </label>
              <textarea
                id="summary"
                value={summary}
                onChange={(e) => setSummary(e.target.value)}
                placeholder="このレシピの概要を簡潔に説明してください"
                rows={3}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500"
                maxLength={300}
              />
              <p className="mt-1 text-xs text-gray-500">
                {summary.length}/300文字
              </p>
            </div>

            {/* 教科 */}
            <div>
              <label htmlFor="subject" className="block text-sm font-medium text-gray-700 mb-1">
                教科（任意）
              </label>
              <select
                id="subject"
                value={subject}
                onChange={(e) => setSubject(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500 cursor-pointer"
              >
                <option value="">選択してください</option>
                <option value="算数">算数</option>
                <option value="数学">数学</option>
                <option value="国語">国語</option>
                <option value="理科">理科</option>
                <option value="社会">社会</option>
                <option value="英語">英語</option>
                <option value="その他">その他</option>
              </select>
            </div>

            {/* 対象学年 */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                対象学年（任意）
              </label>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label htmlFor="grade-min" className="block text-xs text-gray-600 mb-1">
                    最小学年
                  </label>
                  <select
                    id="grade-min"
                    value={gradeMin === null ? '' : gradeMin}
                    onChange={(e) => setGradeMin(e.target.value ? Number(e.target.value) : null)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500 cursor-pointer"
                  >
                    <option value="">選択してください</option>
                    {Array.from({ length: 12 }, (_, i) => i + 1).map((g) => (
                      <option key={g} value={g}>
                        {g <= 6 ? `小${g}` : g <= 9 ? `中${g - 6}` : `高${g - 9}`}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label htmlFor="grade-max" className="block text-xs text-gray-600 mb-1">
                    最大学年
                  </label>
                  <select
                    id="grade-max"
                    value={gradeMax === null ? '' : gradeMax}
                    onChange={(e) => setGradeMax(e.target.value ? Number(e.target.value) : null)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500 cursor-pointer"
                  >
                    <option value="">選択してください</option>
                    {Array.from({ length: 12 }, (_, i) => i + 1).map((g) => (
                      <option key={g} value={g}>
                        {g <= 6 ? `小${g}` : g <= 9 ? `中${g - 6}` : `高${g - 9}`}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
            </div>

            {/* シーン */}
            <div>
              <label htmlFor="scene" className="block text-sm font-medium text-gray-700 mb-1">
                シーン <span className="text-red-500">*</span>
              </label>
              <select
                id="scene"
                value={scene}
                onChange={(e) => setScene(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500 cursor-pointer"
              >
                <option value="">選択してください</option>
                <option value="居場所">居場所</option>
                <option value="家庭">家庭</option>
                <option value="自主学習">自主学習</option>
                <option value="オンライン">オンライン</option>
              </select>
            </div>

            {/* グループ形態 */}
            <div>
              <label htmlFor="group-type" className="block text-sm font-medium text-gray-700 mb-1">
                グループ形態（任意）
              </label>
              <select
                id="group-type"
                value={groupType}
                onChange={(e) => setGroupType(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500 cursor-pointer"
              >
                <option value="">選択してください</option>
                <option value="個別">個別</option>
                <option value="ペア">ペア</option>
                <option value="小グループ">小グループ</option>
                <option value="全体">全体</option>
              </select>
            </div>

            {/* 所要時間 */}
            <div>
              <label htmlFor="time-minutes" className="block text-sm font-medium text-gray-700 mb-1">
                所要時間（任意）
              </label>
              <div className="flex items-center gap-2">
                <input
                  type="number"
                  id="time-minutes"
                  value={timeMinutes === null ? '' : timeMinutes}
                  onChange={(e) => setTimeMinutes(e.target.value ? Number(e.target.value) : null)}
                  placeholder="30"
                  min="1"
                  max="999"
                  className="w-32 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500"
                />
                <span className="text-sm text-gray-600">分</span>
              </div>
            </div>

            {/* 手順 */}
            <div>
              <label htmlFor="steps" className="block text-sm font-medium text-gray-700 mb-1">
                手順 <span className="text-red-500">*</span>
              </label>
              <textarea
                id="steps"
                value={steps}
                onChange={(e) => setSteps(e.target.value)}
                placeholder="具体的な手順を記入してください"
                rows={8}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500"
                maxLength={2000}
              />
              <p className="mt-1 text-xs text-gray-500">
                {steps.length}/2000文字
              </p>
            </div>

            {/* コツ・ポイント */}
            <div>
              <label htmlFor="tips" className="block text-sm font-medium text-gray-700 mb-1">
                コツ・ポイント（任意）
              </label>
              <textarea
                id="tips"
                value={tips}
                onChange={(e) => setTips(e.target.value)}
                placeholder="うまくいくコツやポイントがあれば記入してください"
                rows={4}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500"
                maxLength={1000}
              />
              <p className="mt-1 text-xs text-gray-500">
                {tips.length}/1000文字
              </p>
            </div>

            {/* 注意点 */}
            <div>
              <label htmlFor="cautions" className="block text-sm font-medium text-gray-700 mb-1">
                注意点（任意）
              </label>
              <textarea
                id="cautions"
                value={cautions}
                onChange={(e) => setCautions(e.target.value)}
                placeholder="注意すべき点があれば記入してください"
                rows={4}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500"
                maxLength={1000}
              />
              <p className="mt-1 text-xs text-gray-500">
                {cautions.length}/1000文字
              </p>
            </div>

            {/* 単元ガイド */}
            <div>
              <label htmlFor="material-id" className="block text-sm font-medium text-gray-700 mb-1">
                関連する単元ガイド（任意）
              </label>
              <select
                id="material-id"
                value={materialId || ''}
                onChange={(e) => setMaterialId(e.target.value || null)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500 cursor-pointer"
              >
                <option value="">選択してください</option>
                {materials.map((material) => (
                  <option key={material.id} value={material.id}>
                    {material.title}
                  </option>
                ))}
              </select>
            </div>

            {/* 教材サイト */}
            <div>
              <label htmlFor="site-id" className="block text-sm font-medium text-gray-700 mb-1">
                関連する教材サイト（任意）
              </label>
              <select
                id="site-id"
                value={siteId || ''}
                onChange={(e) => setSiteId(e.target.value || null)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500 cursor-pointer"
              >
                <option value="">選択してください</option>
                {sites.map((site) => (
                  <option key={site.id} value={site.id}>
                    {site.name}
                  </option>
                ))}
              </select>
            </div>

            {/* 投稿者区分 */}
            <div>
              <label htmlFor="role" className="block text-sm font-medium text-gray-700 mb-1">
                投稿者区分（任意）
              </label>
              <select
                id="role"
                value={role}
                onChange={(e) => setRole(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500 cursor-pointer"
              >
                <option value="">選択してください</option>
                <option value="教員">教員</option>
                <option value="居場所スタッフ">居場所スタッフ</option>
                <option value="ボランティア">ボランティア</option>
                <option value="その他">その他</option>
              </select>
            </div>

            {/* ニックネーム */}
            <div>
              <label htmlFor="author-name" className="block text-sm font-medium text-gray-700 mb-1">
                ニックネーム（任意）
              </label>
              <input
                type="text"
                id="author-name"
                value={authorName}
                onChange={(e) => setAuthorName(e.target.value)}
                placeholder="例：たろう先生"
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500"
                maxLength={50}
              />
              <p className="mt-1 text-xs text-gray-500">
                公開時に表示されます（未入力の場合は「匿名」と表示されます）
              </p>
            </div>

            {/* ボタン */}
            <div className="flex gap-4 pt-4">
              <button
                type="button"
                onClick={() => navigate('/recipes')}
                className="flex-1 px-6 py-3 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors whitespace-nowrap cursor-pointer"
                disabled={submitting}
              >
                キャンセル
              </button>
              <button
                type="submit"
                disabled={submitting}
                className="flex-1 px-6 py-3 bg-teal-600 text-white rounded-lg hover:bg-teal-700 transition-colors disabled:bg-gray-400 disabled:cursor-not-allowed whitespace-nowrap cursor-pointer"
              >
                {submitting ? '投稿中...' : '投稿する'}
              </button>
            </div>
          </form>

          {/* 注意事項 */}
          <div className="mt-6 p-4 bg-amber-50 border border-amber-200 rounded-lg">
            <h3 className="text-sm font-medium text-amber-800 mb-2">
              投稿前にご確認ください
            </h3>
            <ul className="text-sm text-amber-700 space-y-1 list-disc list-inside">
              <li>投稿内容は運営チームが確認してから公開されます</li>
              <li>他者の著作権を侵害する内容は投稿しないでください</li>
              <li>個人情報や機密情報を含めないようご注意ください</li>
            </ul>
          </div>
        </div>
      </div>
    </Layout>
  );
}
